import { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import {
  themeAtom,
  emailJobDataAtom,
  emailClientDataAtom,
  imageBreakdownModalOpenAtom,
} from "../-Atoms";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import { v4 as uuidv4 } from "uuid";
import { db, doc, updateDoc } from "../firebaseConfig";

// Import the same BreakdownItemV2 component used in the invoicing tab
import BreakdownItemV2 from "../Calendar/EventDataModalV2/components/JobTab/InvoicingComponents/BreakdownItemV2";

const ImageBreakdownModalV2 = () => {
  const [open, setOpen] = useAtom(imageBreakdownModalOpenAtom);
  const [jobData] = useAtom(emailJobDataAtom);
  const [clientData] = useAtom(emailClientDataAtom);
  const [theme] = useAtom(themeAtom);

  const [imageBreakdown, setImageBreakdown] = useState([]);
  const [showRemoveBreakdownItem, setShowRemoveBreakdownItem] = useState(false);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const [openAddItemDialog, setOpenAddItemDialog] = useState(false);
  const [newItemDescription, setNewItemDescription] = useState("");

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (jobData?.imageBreakdown) {
      setImageBreakdown(jobData.imageBreakdown || []);
    }
  }, [jobData]);

  const isVideoJob = (jobData) => {
    if (jobData?.jobTypeFull?.includes("-V")) return true;
    if (jobData?.jobTypeFull?.includes("-E")) return true;
    return false;
  };

  const handleAddItemClick = () => {
    setOpenAddItemDialog(true);
  };

  const handleAddItemCancel = () => {
    setOpenAddItemDialog(false);
    setNewItemDescription("");
  };

  const handleAddItemConfirm = () => {
    if (!newItemDescription.trim()) {
      return; // Don't create if description is empty
    }

    const newitemObj = {
      id: uuidv4(),
      description: newItemDescription.trim(),
      quantityProcessed: "",
      filesInPost: "",
      quantitySupplied: "",
      itemHidden: false,
    };
    const updatedBreakdown = [...imageBreakdown, newitemObj];
    setImageBreakdown(updatedBreakdown);
    if (jobData?.id) {
      updateDoc(doc(db, "JobData", jobData.id), {
        imageBreakdown: updatedBreakdown,
      }).catch((e) => console.log(e));
    }

    // Reset dialog state
    setOpenAddItemDialog(false);
    setNewItemDescription("");
  };

  const regenerateBreakdownItems = () => {
    if (!clientData?.imageBreakdown) return;

    // Create new breakdown items based on client template
    const newBreakdownItems = clientData?.imageBreakdown.map((template) => ({
      id: uuidv4(),
      description: template.description,
      quantityProcessed: "",
      filesInPost: "",
      quantitySupplied: "",
      itemHidden: false,
      existingItem: true,
      expectedPerHour: template.expectedPerHour,
      costPerFile: template.costPerFile,
      jobTypesFilter: template.jobTypesFilter || [],
    }));

    setImageBreakdown(newBreakdownItems);
    if (jobData?.id) {
      updateDoc(doc(db, "JobData", jobData.id), {
        imageBreakdown: newBreakdownItems,
      }).catch((e) => console.log(e));
    }
  };

  const toggleRemoveBreakdownItem = () => {
    setShowRemoveBreakdownItem(true);
    setTimeout(() => {
      setShowRemoveBreakdownItem(false);
    }, 6000);
  };

  const handleShowHiddenToggle = () => {
    setShowHiddenItems(!showHiddenItems);
  };

  const hasHiddenItems = imageBreakdown.some((item) => item.itemHidden);

  const modalContainer = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 700,
    maxHeight: "80vh",
    outline: "none",
  };

  const modalBody = {
    backgroundColor: theme === "dark" ? "#1f1f1f" : "#f4f5f7",
    color: theme === "dark" ? "#ffffff" : "#000000",
    display: "flex",
    flexDirection: "column",
    minHeight: "60vh",
    maxHeight: "80vh",
    borderRadius: "10px",
    padding: 2,
    overflow: "hidden",
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 1,
      },
    },
  };

  if (!jobData) {
    return (
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalContainer}>
          <Box sx={modalBody}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: theme === "dark" ? "#ffffff" : "#000000",
                }}
              >
                Image Breakdown
              </Typography>
              <IconButton
                onClick={handleClose}
                sx={{
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.7)"
                      : "rgba(0,0,0,0.7)",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography
              sx={{
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.7)"
                    : "rgba(0,0,0,0.7)",
              }}
            >
              No job data available
            </Typography>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalContainer}>
          <Box sx={modalBody}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                px: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: theme === "dark" ? "#ffffff" : "#000000",
                }}
              >
                {isVideoJob(jobData) ? "Video Breakdown" : "Image Breakdown"}
              </Typography>
              <IconButton
                onClick={handleClose}
                sx={{
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.7)"
                      : "rgba(0,0,0,0.7)",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                px: 1,
              }}
            >
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                style={{ height: "100%" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    borderRadius: "8px",
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(35,35,35,0.7)"
                        : "rgba(235,235,235,0.9)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow:
                      theme === "dark"
                        ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                        : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                    padding: 1,
                    height: "100%",
                  }}
                >
                  {/* Controls Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {hasHiddenItems && (
                        <Switch
                          checked={showHiddenItems}
                          onChange={handleShowHiddenToggle}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: theme === "dark" ? "#90caf9" : "#1976d2",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor:
                                  theme === "dark" ? "#90caf9" : "#1976d2",
                              },
                            "& .MuiSwitch-track": {
                              backgroundColor:
                                theme === "dark"
                                  ? "rgba(255,255,255,0.3)"
                                  : "rgba(0,0,0,0.3)",
                            },
                          }}
                        />
                      )}
                      {clientData?.imageBreakdown && (
                        <IconButton
                          onClick={regenerateBreakdownItems}
                          sx={{
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.7)"
                                : "rgba(0,0,0,0.7)",
                            "&:hover": {
                              backgroundColor:
                                theme === "dark"
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.1)",
                              color: theme === "dark" ? "#ffffff" : "#000000",
                            },
                          }}
                        >
                          <ReplayIcon />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={handleAddItemClick}
                        sx={{
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.7)"
                              : "rgba(0,0,0,0.7)",
                          "&:hover": {
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(255,255,255,0.1)"
                                : "rgba(0,0,0,0.1)",
                            color: theme === "dark" ? "#ffffff" : "#000000",
                          },
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                      <IconButton
                        onClick={toggleRemoveBreakdownItem}
                        sx={{
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.7)"
                              : "rgba(0,0,0,0.7)",
                          "&:hover": {
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(255,255,255,0.1)"
                                : "rgba(0,0,0,0.1)",
                            color: theme === "dark" ? "#ffffff" : "#000000",
                          },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Breakdown Items */}
                  <Box sx={{ flex: 1, overflow: "auto" }}>
                    {imageBreakdown.map((item, index) => (
                      <BreakdownItemV2
                        key={item.id}
                        item={item}
                        items={imageBreakdown}
                        setItems={setImageBreakdown}
                        index={index}
                        showHiddenItems={showHiddenItems}
                        showRemoveBreakdownItem={showRemoveBreakdownItem}
                        jobData={jobData}
                        imageBreakdownClient={clientData?.imageBreakdown || []}
                      />
                    ))}
                  </Box>

                  {imageBreakdown.length === 0 && (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 3,
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.6)"
                            : "rgba(0,0,0,0.6)",
                      }}
                    >
                      <Typography variant="body2">
                        No breakdown items yet. Click the + button to add items.
                      </Typography>
                      {clientData?.imageBreakdown?.length > 0 && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Or click the refresh button to load items from the
                          client template.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </motion.div>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Item Dialog */}
      <Dialog
        open={openAddItemDialog}
        onClose={handleAddItemCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Breakdown Item</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a description for the new breakdown item:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            value={newItemDescription}
            onChange={(e) => setNewItemDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                e.preventDefault();
                handleAddItemConfirm();
              }
            }}
            placeholder="e.g. Ghost / Life / Jackets / T-Shirts etc..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddItemCancel}>Cancel</Button>
          <Button
            onClick={handleAddItemConfirm}
            variant="contained"
            disabled={!newItemDescription.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageBreakdownModalV2;
